import { faCheck } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { FloatingLabelPasswordProps } from "@aviary";
import { FloatingLabelPassword } from "@aviary";
import { l } from "@unauthenticated/shared/locales/i18n";

import { validPasswordLength } from "./PasswordValidation";

import * as styles from "./PasswordInput.styles";

interface Props extends Omit<FloatingLabelPasswordProps, "id" | "label"> {
  errors?: ReactNode[];
  value: string;
  isDirty: boolean;
  isLoading: boolean;
  label?: string;
}

const PasswordInput: FC<Props> = ({
  isDirty,
  value,
  errors,
  disabled,
  isLoading,
  label,
  ...rest
}) => {
  const { t } = useTranslation();

  const isValid = validPasswordLength(value);

  const getErrors = () => {
    if (!isDirty) return null;
    if (isValid) return errors;
    return [...(errors || []), t(l.common.LengthValidation)];
  };

  const getDescriptiveText = () => {
    if (!isDirty || !isValid) return null;

    return (
      <>
        <FontAwesomeIcon icon={faCheck} size="sm" css={styles.icon} />
        {t(l.common.LengthConfirmation)}
      </>
    );
  };

  return (
    <FloatingLabelPassword
      descriptiveText={getDescriptiveText()}
      data-testid="password"
      id="password"
      name="password"
      data-e2e="password"
      label={label || t(l.common.Password)}
      value={value}
      errors={getErrors()}
      disabled={disabled || isLoading}
      {...rest}
    />
  );
};
export { PasswordInput };
